// Third Party
import React, { Component } from 'react';
import { connect } from 'react-redux';

// Actions
import { ReduxState } from '../../../../redux/modules/types';
import { addSearchItemLog } from '../../../../redux/modules/activity-log';
import { clearResults } from '../../../../redux/modules/advanced-search';
import {
  addBenchmarkToComparisonById,
  addFundToComparisonById,
  addFundsToComparison,
  addPortfolioToComparisonById,
  addStockToComparisonById,
  addBenchmarkPortfolioToComparisonById,
  addBenchmarksToComparison,
} from '../../../../redux/modules/comparison';

// Components
import { QuickSearch } from '../../../components/common';
import { AdvancedSearchModal } from '../../../components/modals';
import { PrimaryLink } from '../../../components/buttons';

// Utils
import { QUICK_SEARCH_REDUCER_NAME, QUICK_SEARCH_TYPE } from '../../../../redux/modules/quick-search';
import { FormattedMessage, formatMessage } from '../../../../utils/i18n';
import { COMPARISON_ITEM_TYPES, QUICK_SEARCH_SUBTYPES, ADVANCED_SEARCH_OPTIONS } from '../../../../utils/constants';
import { SEARCH_ITEM_SOURCE, SEARCH_ITEM_DEST } from '../../../../utils/activity-log';
import { showError } from '../../../../utils/errors';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import Fund from '@/types/fund';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import QuickSearchItem from '@/types/quick-search-item';
import Nullable from '@/types/nullable';
import User from '@/types/user';

type StateProps = {
  selectedAdvancedSearchFunds: Fund[];
  selectedAdvancedSearchBenchmarks: ComparisonBenchmark[];
  isIncomeTab?: boolean;
};

type DispatchProps = {
  addBenchmarkToComparisonById: (id: string) => Promise<void>;
  addFundToComparisonById: (id: string) => Promise<void>;
  addFundsToComparison: (funds: Fund[]) => Promise<void>;
  addPortfolioToComparisonById: (quickSearchItem: QuickSearchItem) => Promise<void>;
  addSearchItemLog: (id: string, type: string, source: string, dest: string) => void;
  addStockToComparisonById: (id: string) => Promise<void>;
  clearResults: () => void;
  addBenchmarkPortfolioToComparisonById: (id: string) => Promise<void>;
  addBenchmarksToComparison: (benchmarks: ComparisonBenchmark[]) => Promise<void>;
};

type ParentProps = {
  dark?: boolean;
  user: User;
};

type State = {
  showModal: boolean;
  filterOption: Nullable<QUICK_SEARCH_TYPE>;
  searchOption: ADVANCED_SEARCH_OPTIONS;
};

type Props = StateProps & DispatchProps & ParentProps;

class LegacyComparisonQuickSearch extends Component<Props, State> {
  readonly state: State = {
    showModal: false,
    filterOption: null,
    searchOption: ADVANCED_SEARCH_OPTIONS.FUNDS,
  };

  onQuickSearchSelection = (item: QuickSearchItem) => {
    switch (item.type) {
      case COMPARISON_ITEM_TYPES.FUND: {
        this.props
          .addFundToComparisonById(item._id)
          .then(() => {
            this.props.addSearchItemLog(item._id, item.type, SEARCH_ITEM_SOURCE.QUICKSEARCH, SEARCH_ITEM_DEST.COMPARISON);
          })
          .catch(showError);
        return;
      }
      case COMPARISON_ITEM_TYPES.PORTFOLIO: {
        this.props.addPortfolioToComparisonById(item).catch(showError);
        return;
      }
      case COMPARISON_ITEM_TYPES.STOCK: {
        this.props
          .addStockToComparisonById(item._id)
          .then(() => {
            this.props.addSearchItemLog(item._id, item.type, SEARCH_ITEM_SOURCE.QUICKSEARCH, SEARCH_ITEM_DEST.COMPARISON);
          })
          .catch(showError);
        return;
      }
      case COMPARISON_ITEM_TYPES.BENCHMARK: {
        if (item.subType && item.subType === QUICK_SEARCH_SUBTYPES.BENCHMARK_PORTFOLIO) {
          this.props.addBenchmarkPortfolioToComparisonById(item._id).catch(showError);
        } else {
          this.props
            .addBenchmarkToComparisonById(item._id)
            .then(() => {
              this.props.addSearchItemLog(item._id, item.type, SEARCH_ITEM_SOURCE.QUICKSEARCH, SEARCH_ITEM_DEST.COMPARISON);
            })
            .catch(showError);
        }
      }
    }
  };

  closeAdvancedSearchModal = () => {
    this.setState({ showModal: false }, this.props.clearResults);
  };

  showAdvancedSearchModal = () => this.setState({ showModal: true });

  onAdvancedSearchConfirm = () => {
    const { selectedAdvancedSearchFunds, selectedAdvancedSearchBenchmarks } = this.props;
    const { searchOption } = this.state;

    switch (searchOption) {
      case ADVANCED_SEARCH_OPTIONS.FUNDS: {
        this.props.addFundsToComparison(selectedAdvancedSearchFunds);
        break;
      }
      case ADVANCED_SEARCH_OPTIONS.BENCHMARKS: {
        this.props.addBenchmarksToComparison(selectedAdvancedSearchBenchmarks);
        break;
      }
    }

    this.closeAdvancedSearchModal();
  };

  renderSearchPrimaryLink = () => {
    const { filterOption } = this.state;

    const i18nKey = filterOption && filterOption === QUICK_SEARCH_TYPE.BENCHMARKS ? 'Comparison.TryBenchmarkSearch' : 'Comparison.TryFundSearch';

    return (
      <PrimaryLink onClick={this.showAdvancedSearchModal} className="search-primary-link">
        <FormattedMessage id={i18nKey} />
        <i className="icon-arrow-up-right2" />
      </PrimaryLink>
    );
  };

  onFilterChange = (option: Nullable<QUICK_SEARCH_TYPE>) => {
    this.setState({ filterOption: option });

    switch (option) {
      case QUICK_SEARCH_TYPE.BENCHMARKS: {
        return this.setState({ searchOption: ADVANCED_SEARCH_OPTIONS.BENCHMARKS });
      }
      default: {
        return this.setState({ searchOption: ADVANCED_SEARCH_OPTIONS.FUNDS });
      }
    }
  };

  onChangeOption = (option: ADVANCED_SEARCH_OPTIONS) => {
    this.setState({ searchOption: option });
  };

  getAllowedTypes = () => {
    const { isIncomeTab, user } = this.props;

    if (isIncomeTab) {
      return [
        QUICK_SEARCH_TYPE.FUNDS,
        // TODO - uncomment when we fix stock data
        // QUICK_SEARCH_TYPE.STOCKS,
        QUICK_SEARCH_TYPE.PORTFOLIOS,
      ];
    }

    const allowedTypes = [
      QUICK_SEARCH_TYPE.FUNDS,
      QUICK_SEARCH_TYPE.STOCKS,
      QUICK_SEARCH_TYPE.BONDS,
      userHasPermissionEnabled(user, PERMISSIONS.USER_MODELS, PERMISSIONS.ENTERPRISE_MODELS_LIST) ? QUICK_SEARCH_TYPE.PORTFOLIOS : null,
      QUICK_SEARCH_TYPE.BENCHMARKS,
    ];

    return allowedTypes.filter(type => type !== null) as Array<QUICK_SEARCH_TYPE>;
  };

  getModalActionLabel = () => {
    const { searchOption } = this.state;

    if (searchOption === ADVANCED_SEARCH_OPTIONS.BENCHMARKS) {
      return formatMessage('AdvancedSearchModal.AddBenchmarks');
    }

    return formatMessage('AdvancedSearchModal.AddFunds');
  };

  render() {
    const { dark, selectedAdvancedSearchFunds, selectedAdvancedSearchBenchmarks } = this.props;
    const { showModal, searchOption } = this.state;

    return (
      <>
        <div className="comparison-quick-search d-flex">
          <QuickSearch
            dark={dark}
            targetReducer={QUICK_SEARCH_REDUCER_NAME.COMPARISON}
            className="comparison-fund-search"
            onItemSelection={this.onQuickSearchSelection}
            searchAction={this.renderSearchPrimaryLink()}
            withItemScreener
            onScreenerButtonClick={this.showAdvancedSearchModal}
            allowedTypes={this.getAllowedTypes()}
            onFilterChange={this.onFilterChange}
            autoFocus={false}
            showSavedOnFocus
          />
        </div>

        <AdvancedSearchModal
          show={showModal}
          onCancel={this.closeAdvancedSearchModal}
          onConfirm={this.onAdvancedSearchConfirm}
          actionDisabled={!selectedAdvancedSearchFunds.length && !selectedAdvancedSearchBenchmarks.length}
          selectedOption={searchOption}
          actionLabel={this.getModalActionLabel()}
          onChangeOption={this.onChangeOption}
        />
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  selectedAdvancedSearchFunds: state.advancedSearch.fund.selectedFunds,
  selectedAdvancedSearchBenchmarks: state.advancedSearch.benchmark.selectedBenchmarks,
});

export default connect(mapStateToProps, {
  addBenchmarkToComparisonById,
  addFundToComparisonById,
  addFundsToComparison,
  addPortfolioToComparisonById,
  addSearchItemLog,
  addStockToComparisonById,
  clearResults,
  addBenchmarkPortfolioToComparisonById,
  addBenchmarksToComparison,
})(LegacyComparisonQuickSearch);
